import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, AlertTriangle, CheckCircle, XCircle, PartyPopper } from 'lucide-react';

// Sims-style plumbob diamond
const Plumbob = ({ color = 'green', className = '' }) => (
  <motion.svg
    viewBox="0 0 24 24"
    className={`w-8 h-8 ${className}`}
    animate={{ 
      rotateY: [0, 180, 360],
      scale: [1, 1.1, 1]
    }}
    transition={{ 
      duration: 3, 
      repeat: Infinity, 
      ease: 'easeInOut' 
    }}
  >
    <polygon
      points="12,2 22,12 12,22 2,12"
      fill={color === 'green' ? 'url(#plumbob-green)' : color === 'yellow' ? 'url(#plumbob-yellow)' : 'url(#plumbob-red)'}
      stroke={color === 'green' ? '#22c55e' : color === 'yellow' ? '#eab308' : '#ef4444'}
      strokeWidth="1"
    />
    <defs>
      <linearGradient id="plumbob-green" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4ade80" />
        <stop offset="50%" stopColor="#22c55e" />
        <stop offset="100%" stopColor="#16a34a" />
      </linearGradient>
      <linearGradient id="plumbob-yellow" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fde047" />
        <stop offset="50%" stopColor="#eab308" />
        <stop offset="100%" stopColor="#ca8a04" />
      </linearGradient>
      <linearGradient id="plumbob-red" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f87171" />
        <stop offset="50%" stopColor="#ef4444" />
        <stop offset="100%" stopColor="#dc2626" />
      </linearGradient>
    </defs>
  </motion.svg>
);

// Speech bubble tail
const SpeechTail = ({ variant = 'success' }) => {
  const colors = {
    success: '#dcfce7',
    warning: '#fef9c3',
    error: '#fee2e2',
    info: '#e0e7ff',
    party: '#fce7f3'
  };
  
  return (
    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
      <div 
        className="w-0 h-0"
        style={{
          borderLeft: '12px solid transparent',
          borderRight: '12px solid transparent',
          borderTop: `16px solid ${colors[variant]}`
        }}
      />
    </div>
  );
};

export function SimsConfirmDialog({ 
  open, 
  onClose, 
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure?',
  confirmText = 'Yes, do it!',
  cancelText = 'Nevermind',
  variant = 'warning', // success, warning, error, info, party
  icon: CustomIcon = null
}) {
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    await onConfirm();
    setIsConfirming(false);
  };

  const variants = {
    success: {
      bg: 'bg-gradient-to-br from-green-50 via-emerald-50 to-[#EBF0F5]',
      border: 'border-green-200',
      iconBg: 'bg-green-100',
      icon: <CheckCircle className="h-8 w-8 text-green-600" />,
      plumbob: 'green',
      confirmBtn: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
    },
    warning: {
      bg: 'bg-gradient-to-br from-yellow-50 via-[#F7F1E8] to-[#F7F1E8]',
      border: 'border-yellow-200',
      iconBg: 'bg-yellow-100',
      icon: <AlertTriangle className="h-8 w-8 text-yellow-600" />,
      plumbob: 'yellow',
      confirmBtn: 'bg-gradient-to-r from-yellow-500 to-[#C8A97E] hover:from-yellow-600 hover:to-[#B3956B]'
    },
    error: {
      bg: 'bg-gradient-to-br from-red-50 via-[#F7F1E8] to-[#F7F1E8]',
      border: 'border-red-200',
      iconBg: 'bg-red-100',
      icon: <XCircle className="h-8 w-8 text-red-600" />,
      plumbob: 'red',
      confirmBtn: 'bg-gradient-to-r from-red-500 to-[#C8A97E] hover:from-red-600 hover:to-[#B3956B]'
    },
    info: {
      bg: 'bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]',
      border: 'border-[#D7E0EA]',
      iconBg: 'bg-[#EBF0F5]',
      icon: <Sparkles className="h-8 w-8 text-[#2B4C7E]" />,
      plumbob: 'green',
      confirmBtn: 'bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] hover:from-[#2B4C7E] hover:to-[#2B4C7E]'
    },
    party: {
      bg: 'bg-gradient-to-br from-[#F7F1E8] via-[#EBF0F5] to-[#EBF0F5]',
      border: 'border-[#EAD9BF]',
      iconBg: 'bg-[#F2E9D9]',
      icon: <PartyPopper className="h-8 w-8 text-[#B3956B]" />,
      plumbob: 'green',
      confirmBtn: 'bg-gradient-to-r from-[#C8A97E] to-[#2B4C7E] hover:from-[#B3956B] hover:to-[#2B4C7E]'
    }
  };

  const style = variants[variant] || variants.warning;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="p-0 border-0 bg-transparent shadow-none max-w-md">
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            >
              {/* Floating Plumbob */}
              <div className="flex justify-center mb-2">
                <Plumbob color={style.plumbob} />
              </div>

              {/* Speech Bubble */}
              <div className={`relative ${style.bg} ${style.border} border-2 rounded-3xl p-6 shadow-xl`}>
                {/* Decorative dots */}
                <div className="absolute top-2 left-4 flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-white/60" />
                  <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
                  <div className="w-1 h-1 rounded-full bg-white/20" />
                </div>

                <div className="text-center">
                  {/* Icon */}
                  <motion.div 
                    className={`w-16 h-16 ${style.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}
                    animate={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {CustomIcon || style.icon}
                  </motion.div>

                  {/* Title */}
                  <motion.h3 
                    className="text-xl font-bold text-gray-800 mb-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    {title}
                  </motion.h3>

                  {/* Message */}
                  <motion.p 
                    className="text-gray-600 mb-6"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    {message}
                  </motion.p>

                  {/* Buttons */}
                  <div className="flex gap-3 justify-center">
                    <Button
                      variant="outline"
                      onClick={onClose}
                      className="rounded-full px-6 border-2 font-medium hover:bg-gray-100"
                    >
                      {cancelText}
                    </Button>
                    <Button
                      onClick={handleConfirm}
                      disabled={isConfirming}
                      className={`rounded-full px-6 text-white font-medium shadow-lg ${style.confirmBtn}`}
                    >
                      {isConfirming ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        >
                          <Sparkles className="h-5 w-5" />
                        </motion.div>
                      ) : (
                        confirmText
                      )}
                    </Button>
                  </div>
                </div>

                <SpeechTail variant={variant} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// Toast-style notification (non-blocking)
export function SimsToast({ 
  show, 
  onHide, 
  message, 
  variant = 'success',
  duration = 3000 
}) {
  useEffect(() => {
    if (show && duration) {
      const timer = setTimeout(onHide, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration, onHide]);

  const variants = {
    success: 'from-green-400 to-emerald-500 border-green-300',
    warning: 'from-yellow-400 to-[#C8A97E] border-yellow-300',
    error: 'from-red-400 to-[#C8A97E] border-red-300',
    info: 'from-[#3A5C8F] to-[#2B4C7E] border-[#B9C7D6]',
    party: 'from-[#D0B384] to-[#2B4C7E] border-[#DCC39A]'
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50"
        >
          <div className={`bg-gradient-to-r ${variants[variant]} text-white px-6 py-3 rounded-full shadow-lg border-2 flex items-center gap-3`}>
            <Plumbob color={variant === 'error' ? 'red' : variant === 'warning' ? 'yellow' : 'green'} className="w-6 h-6" />
            <span className="font-medium">{message}</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default SimsConfirmDialog;
